/**
 * @file Bandeau de progression du mode (facon original) : barre fine pleine largeur avec %, piste des niveaux du stage (chat = niveau courant,
 * dernier noeud = boss), titre (Stage 2-5 / Vague 3/30), pastille chrono / recolte libre, barre de vie du boss/barriere, bouton Defi/Quitter.
 */
import { formatBig, formatDuration } from '@shared/format';
import { Component } from '../core/Component';
import { h, setText, setVar, toggleClass } from '../core/dom';
import { send } from '../core/feedback';
import type { UiContext } from '../UiContext';

const DANGER_SECONDS = 10;
const TICK_SECONDS = 5;

export class StageBar extends Component {
  private title!: HTMLElement;
  private stripBar!: HTMLElement;
  private stripText!: HTMLElement;
  private track!: HTMLElement;
  private nodes: HTMLElement[] = [];
  private timer!: HTMLElement;
  private bossRow!: HTMLElement;
  private bossBar!: HTMLElement;
  private bossText!: HTMLElement;
  private bossInfo!: HTMLElement;
  private barrierRow!: HTMLElement;
  private barrierBar!: HTMLElement;
  private barrierText!: HTMLElement;
  private challenge!: HTMLElement;
  private exit!: HTMLElement;
  private lastTickSecond = -1;

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'stagebar');
  }

  protected build(): void {
    const t = this.ctx.i18n;
    // Barre fine en haut de l'ecran : avancement du niveau en %.
    this.stripBar = h('div', { class: 'progress sb-strip-bar' });
    this.stripText = h('span', { class: 'sb-strip-text outlined-text' });
    const strip = h('div', { class: 'sb-strip', 'data-tutorial': 'stage-progress' }, this.stripBar, this.stripText);

    // Piste des niveaux du stage : un noeud par niveau, le chat marque le niveau courant, le dernier est le boss.
    const levels = this.ctx.config.balance.stage.levelsPerStage;
    this.nodes = Array.from({ length: levels }, (_, i) => h('span', { class: `sb-node${i === levels - 1 ? ' boss' : ''}` }));
    this.track = h('div', { class: 'sb-track' }, h('span', { class: 'sb-rail' }), ...this.nodes);

    this.title = h('div', { class: 'sb-title outlined-text' });
    this.timer = h('div', { class: 'sb-timer' });

    this.bossText = h('span', { class: 'sb-bartext' });
    this.bossBar = h('div', { class: 'progress boss' });
    this.bossRow = h('div', { class: 'sb-hp is-hidden' }, h('span', { class: 'sb-hplabel' }, t.t('hud.boss')), h('div', { class: 'sb-barwrap' }, this.bossBar, this.bossText));

    this.bossInfo = h('div', { class: 'sb-bossinfo is-hidden' });

    this.barrierText = h('span', { class: 'sb-bartext' });
    this.barrierBar = h('div', { class: 'progress barrier' });
    this.barrierRow = h('div', { class: 'sb-hp is-hidden' }, h('span', { class: 'sb-hplabel' }, t.t('hud.barrier')), h('div', { class: 'sb-barwrap' }, this.barrierBar, this.barrierText));

    this.challenge = h(
      'button',
      { class: 'btn challenge is-hidden', type: 'button', 'data-ui-block': true, 'data-fast': true, 'data-tutorial': 'challenge-button', onclick: () => void send(this.ctx, { type: 'mode.challenge' }) },
      t.t('hud.challenge'),
    );
    this.exit = h('button', { class: 'btn exit is-hidden', type: 'button', 'data-ui-block': true, 'data-fast': true, onclick: () => void send(this.ctx, { type: 'mode.exit' }) }, t.t('hud.exitMode'));

    const center = h(
      'div',
      { class: 'sb-center' },
      this.track,
      h('div', { class: 'sb-main' }, this.title, this.bossRow, this.bossInfo, this.barrierRow),
      h('div', { class: 'sb-actions' }, this.timer, this.challenge, this.exit),
    );
    this.el.append(strip, center);
  }

  override update(_dt: number): void {
    const hud = this.ctx.sim.getView().hud();
    const { i18n } = this.ctx;
    if (!hud) {
      toggleClass(this.el, 'is-hidden', true);
      return;
    }
    toggleClass(this.el, 'is-hidden', false);
    setText(this.title, i18n.tDyn(hud.titleKey, hud.titleParams));

    const ratio = hud.progress && hud.progress.max > 0 ? Math.min(1, hud.progress.current / hud.progress.max) : 0;
    setVar(this.stripBar, '--progress', ratio.toFixed(3));
    setText(this.stripText, hud.progress ? `${Math.round(ratio * 100)}%` : '');

    // Piste : uniquement pour les stages du Main (titre 'hud.stage' {stage, level}).
    const level = typeof hud.titleParams.level === 'number' ? hud.titleParams.level : null;
    toggleClass(this.track, 'is-hidden', hud.titleKey !== 'hud.stage' || level === null);
    if (level !== null) {
      this.nodes.forEach((n, i) => {
        toggleClass(n, 'done', i < level - 1);
        toggleClass(n, 'current', i === level - 1);
      });
    }

    // chrono / mode libre
    if (hud.timeLeft !== null) {
      setText(this.timer, formatDuration(Math.ceil(hud.timeLeft)));
      const danger = hud.timeLeft <= DANGER_SECONDS && hud.phase === 'running';
      toggleClass(this.timer, 'danger', danger);
      const sec = Math.ceil(hud.timeLeft);
      if (hud.phase === 'running' && hud.timeLeft > 0 && hud.timeLeft <= TICK_SECONDS && sec !== this.lastTickSecond) {
        this.lastTickSecond = sec;
        this.ctx.sfx('tick');
      }
      if (hud.timeLeft > TICK_SECONDS) this.lastTickSecond = -1;
    } else {
      toggleClass(this.timer, 'danger', false);
      setText(this.timer, hud.canChallenge ? i18n.t('hud.farming') : '');
      this.lastTickSecond = -1;
    }
    toggleClass(this.timer, 'is-hidden', this.timer.textContent === '');

    // boss / barriere
    toggleClass(this.bossRow, 'is-hidden', hud.boss === null);
    if (hud.boss) {
      const r = hud.boss.max.gt(0) ? Math.max(0, Math.min(1, hud.boss.hp.div(hud.boss.max).toNumber())) : 0;
      setVar(this.bossBar, '--progress', r.toFixed(3));
      setText(this.bossText, `${formatBig(hud.boss.hp)} / ${formatBig(hud.boss.max)}`);
    }
    // phase du Boss / du Raid : « Phase 2/3 », carapace (pylones a detruire) ou oeufs a surveiller
    const info = hud.bossInfo;
    toggleClass(this.bossInfo, 'is-hidden', info === null);
    if (info) {
      let line = `${i18n.t('hud.bossLevel', { n: info.level })} · ${i18n.t('hud.bossPhase', { n: info.phase, max: info.phases })}`;
      if (info.shield > 0) line += ` · ${i18n.t('hud.bossShield', { n: info.shield })}`;
      else if (info.eggs > 0) line += ` · ${i18n.t('hud.raidEggs', { n: info.eggs })}`;
      setText(this.bossInfo, line);
      toggleClass(this.bossInfo, 'shield', info.shield > 0);
      toggleClass(this.bossInfo, 'eggs', info.shield === 0 && info.eggs > 0);
    }
    toggleClass(this.barrierRow, 'is-hidden', hud.barrier === null);
    if (hud.barrier) {
      const r = hud.barrier.max > 0 ? Math.max(0, Math.min(1, hud.barrier.hp / hud.barrier.max)) : 0;
      setVar(this.barrierBar, '--progress', r.toFixed(3));
      setText(this.barrierText, `${Math.max(0, Math.ceil(hud.barrier.hp))} / ${hud.barrier.max}`);
    }

    toggleClass(this.challenge, 'is-hidden', !hud.canChallenge);
    toggleClass(this.exit, 'is-hidden', hud.mode === 'main');
  }
}
